import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import services from './remoteServices';
import {userTransformer} from '../deserializers/stores';
import {UserDetails, UserQueryParam, UserDetailsQuery} from '../models/user';

export type UserStatus = {
  id: string;
};

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${services.user}/api`,
    credentials: 'include'
  }),
  endpoints: builder => ({
    fetchUserWithout401Retry: builder.query<UserDetails, UserQueryParam | void>(
      {
        async queryFn(params: UserQueryParam, api, extraOptions, baseQuery) {
          const storeSlug = params?.storeSlug;
          const url = storeSlug
            ? `/users/current_user?store_id=${storeSlug}`
            : '/users/current_user';
          const result = await baseQuery({
            url,
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
            }
          });

          if (result.error?.status === 401) {
            return {
              data: {
                unauthenticated: true,
                email: null,
                role: null,
                storeRoles: null
              } as UserDetails
            };
          }

          if (result.error) {
            return {error: result.error};
          }

          return {data: userTransformer(result.data as UserDetailsQuery)};
        }
      }
    ),
    fetchUser: builder.query<UserDetails, UserQueryParam | void>({
      query: (params: UserQueryParam) => {
        const storeSlug = params?.storeSlug;
        const url = storeSlug
          ? `/users/current_user?store_id=${storeSlug}`
          : '/users/current_user';
        return {
          url,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
          }
        };
      },
      transformResponse: userTransformer
    }),
    logoutUser: builder.query<UserStatus, string>({
      query: (id: string) => `/users/${id}`
    })
  })
});

export const {
  useLazyFetchUserQuery,
  useFetchUserQuery,
  useLazyLogoutUserQuery,
  useLazyFetchUserWithout401RetryQuery
} = userApi;
